import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Skeleton,
    Select,
    MenuItem,
    ListSubheader,
    TextField,
    ToggleButton,
    Tooltip,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BoltIcon from '@mui/icons-material/Bolt';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { SectionCard } from './shared';
import { hoverScrollbar } from '../../../styles/hoverScrollbar';
import { useGlobalWatchlists } from '../api/useGlobalWatchlists';
import {
    useConvictionScreener,
    useEventMarkets,
    CONVICTION_METRICS,
    DEFAULT_METRIC,
    VALID_POLYMARKET_TAGS,
    VALID_EVENT_CATEGORIES,
    type ConvictionMetric,
    type ConvictionMarket,
    type EventSiblingMarket,
    type MetricDef,
    type DominantSide,
} from '../api/useConvictionScreener';

// ---------------------------------------------------------------------------
// Formatting helpers (same conventions as WatchlistView)
// ---------------------------------------------------------------------------

function formatUsd(v: number | null): string {
    if (v == null) return '—';
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
    return `$${v.toFixed(0)}`;
}

function formatCount(v: number | null): string {
    if (v == null) return '—';
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
    return String(v);
}

function formatProb(v: number | null): string {
    if (v == null) return '—';
    return `${(v * 100).toFixed(0)}%`;
}

function formatSignedProb(v: number | null): string {
    if (v == null) return '';
    const pts = v * 100;
    const sign = pts >= 0 ? '+' : '−';
    return `${sign}${Math.abs(pts).toFixed(0)}%`;
}

function formatRatio(v: number | null): string {
    if (v == null) return '—';
    return v.toFixed(3);
}

function formatMetricValue(metric: MetricDef | undefined, v: number | null): string {
    if (v == null) return '—';
    switch (metric?.format) {
        case 'usd':
            return formatUsd(v);
        case 'count':
            return formatCount(v);
        case 'prob':
            return formatProb(v);
        default:
            return formatRatio(v);
    }
}

function formatRelativeTime(iso: string | null): string {
    if (!iso) return '—';
    const then = new Date(iso).getTime();
    const diffMs = Date.now() - then;
    const mins = Math.round(diffMs / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
}

function prettyTag(slug: string): string {
    return slug.replace(/-/g, ' ');
}

function outcomeProbability(
    p: number | null,
    dominantSide: DominantSide | null
): number | null {
    if (p == null) return null;

    return dominantSide === 'NO'
        ? 1 - p
        : p;
}

// Average entry price the dominant side actually paid for the token it holds.
// cw_avg_entry_price_yes / _no come straight off breakdown.group_aggregation
// and are already oriented to the side that bought, so no inversion is needed:
// when dominant_side === 'NO' the group bought the NO token at _no, which is
// the same token the conviction-outcome price (outcomeMarketProb) refers to.
function avgEntryForSide(market: ConvictionMarket): number | null {
    const ga = market.breakdown?.group_aggregation as
        | {
            cw_avg_entry_price_yes?: number | null;
            cw_avg_entry_price_no?: number | null;
        }
        | undefined;
    if (!ga) return null;
    const raw =
        market.dominant_side === 'NO'
            ? ga.cw_avg_entry_price_no
            : ga.cw_avg_entry_price_yes;
    return typeof raw === 'number' && !Number.isNaN(raw) ? raw : null;
}

// Everything the row + the aggregate bar need, computed once per market so the
// two stay in lockstep.
//   impliedDelta = live outcome price − traders' capital-weighted implied
//   entryDelta   = live outcome price − the price the group entered at
function computeRowMetrics(market: ConvictionMarket): {
    outcomeMarketProb: number | null;
    outcomeImpliedProb: number | null;
    avgEntry: number | null;
    impliedDelta: number | null;
    entryDelta: number | null;
} {
    const outcomeMarketProb = outcomeProbability(
        market.current_yes_prob,
        market.dominant_side
    );
    const outcomeImpliedProb = outcomeProbability(
        market.raw_implied,
        market.dominant_side
    );
    const avgEntry = avgEntryForSide(market);
    const impliedDelta =
        outcomeImpliedProb != null && outcomeMarketProb != null
            ? outcomeMarketProb - outcomeImpliedProb
            : null;
    const entryDelta =
        avgEntry != null && outcomeMarketProb != null
            ? outcomeMarketProb - avgEntry
            : null;
    return { outcomeMarketProb, outcomeImpliedProb, avgEntry, impliedDelta, entryDelta };
}

function mean(values: number[]): number | null {
    if (values.length === 0) return null;
    return values.reduce((a, b) => a + b, 0) / values.length;
}

// Truncate-with-tooltip cell text. The cell stays a fixed width and shows an
// ellipsis; the full text is available on hover as a fallback.
function EllipsisText({
    text,
    maxWidth,
    sx,
}: {
    text: string;
    maxWidth: number;
    sx?: object;
}) {
    return (
        <Tooltip title={text} placement="top-start" enterDelay={400} arrow>
            <Box
                component="span"
                sx={{
                    display: 'block',
                    maxWidth,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    ...sx,
                }}
            >
                {text}
            </Box>
        </Tooltip>
    );
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

// YES means the group bought token0 (the named outcome); NO means token1.
function SideBadge({ side }: { side: DominantSide | null }) {
    const theme = useTheme();
    if (!side) {
        return (
            <Typography sx={{ fontSize: '10px', color: 'text.disabled' }}>—</Typography>
        );
    }
    const color = side === 'YES' ? theme.palette.success.main : theme.palette.error.main;
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                fontSize: '9.5px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                color,
                border: `1px solid ${alpha(color, 0.5)}`,
                bgcolor: alpha(color, 0.08),
                borderRadius: 0.75,
                px: 0.625,
                py: '1px',
            }}
        >
            {side}
        </Box>
    );
}

// The one signature element: a compact bar showing how capital splits across
// Yes/No, with the dominant side carrying the stronger weight.
function CapitalSplitBar({ yes, no }: { yes: number; no: number }) {
    const theme = useTheme();
    const total = yes + no;
    const yesFrac = total > 0 ? yes / total : 0.5;
    return (
        <Box sx={{ mt: '3px' }}>
            <Box
                sx={{
                    display: 'flex',
                    height: 3,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: alpha(theme.palette.text.disabled, 0.15),
                }}
            >
                <Box
                    sx={{
                        width: `${yesFrac * 100}%`,
                        bgcolor: alpha(theme.palette.success.main, 0.75),
                    }}
                />
                <Box
                    sx={{
                        width: `${(1 - yesFrac) * 100}%`,
                        bgcolor: alpha(theme.palette.error.main, 0.6),
                    }}
                />
            </Box>
        </Box>
    );
}

// Clickable polymarket-tag chip. Clicking toggles the tag in the active filter.
function TagChip({
    tag,
    active,
    onClick,
}: {
    tag: string;
    active?: boolean;
    onClick?: (tag: string) => void;
}) {
    const theme = useTheme();
    return (
        <Box
            component="span"
            onClick={
                onClick
                    ? e => {
                        e.stopPropagation();
                        onClick(tag);
                    }
                    : undefined
            }
            sx={{
                display: 'inline-block',
                fontSize: '9.5px',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                color: active ? theme.palette.primary.main : 'text.secondary',
                border: 1,
                borderColor: active
                    ? alpha(theme.palette.primary.main, 0.5)
                    : 'divider',
                bgcolor: active
                    ? alpha(theme.palette.primary.main, 0.08)
                    : 'transparent',
                borderRadius: 0.75,
                px: 0.625,
                py: '1px',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.12s',
                '&:hover': onClick ? { borderColor: 'primary.main' } : undefined,
            }}
        >
            {prettyTag(tag)}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Client-side sort over the already-fetched page
// ---------------------------------------------------------------------------

type ClientSortKey =
    | 'sort_value'
    | 'capital_usd_yes'
    | 'capital_usd_no'
    | 'trader_count'
    | 'current_yes_prob'
    | 'raw_implied'
    | 'avg_entry'
    | 'signal_count';

type SortDir = 'asc' | 'desc';

function clientSortValue(m: ConvictionMarket, key: ClientSortKey): number {
    switch (key) {
        case 'sort_value':
            return m.sort_value ?? -Infinity;
        case 'capital_usd_yes':
            return m.capital_usd_yes ?? -Infinity;
        case 'capital_usd_no':
            return m.capital_usd_no ?? -Infinity;
        case 'trader_count':
            return (m.n_traders_yes ?? 0) + (m.n_traders_no ?? 0);
        case 'current_yes_prob':
            return m.current_yes_prob ?? -Infinity;
        case 'raw_implied':
            return m.raw_implied ?? -Infinity;
        case 'avg_entry':
            return avgEntryForSide(m) ?? -Infinity;
        case 'signal_count':
            return m.signal_count ?? -Infinity;
    }
}

function SortableTh({
    label,
    sortKey,
    currentKey,
    currentDir,
    onSort,
    style,
}: {
    label: string;
    sortKey: ClientSortKey;
    currentKey: ClientSortKey | null;
    currentDir: SortDir;
    onSort: (key: ClientSortKey) => void;
    style?: React.CSSProperties;
}) {
    const active = currentKey === sortKey;
    return (
        <th
            onClick={() => onSort(sortKey)}
            style={{ ...style, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        >
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    opacity: active ? 1 : undefined,
                }}
            >
                {label}
                {active ? (
                    currentDir === 'asc' ? (
                        <ArrowUpwardIcon sx={{ fontSize: 10 }} />
                    ) : (
                        <ArrowDownwardIcon sx={{ fontSize: 10 }} />
                    )
                ) : (
                    <Box sx={{ width: 10, display: 'inline-block' }} />
                )}
            </Box>
        </th>
    );
}

// ---------------------------------------------------------------------------
// Expanded event panel — sibling markets for a clicked row
// ---------------------------------------------------------------------------

function EventExpandPanel({
    eventId,
    group,
}: {
    eventId: string;
    group: string;
}) {
    const theme = useTheme();
    const { data, loading, error } = useEventMarkets(eventId, group);

    if (loading) {
        return (
            <Box sx={{ p: 1.5 }}>
                {[...Array(3)].map((_, i) => (
                    <Skeleton
                        key={i}
                        variant="rectangular"
                        height={22}
                        sx={{ mb: 0.5, borderRadius: 0.5 }}
                    />
                ))}
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 1.5 }}>
                <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                    Couldn't load the rest of this event. Try expanding it again.
                </Typography>
            </Box>
        );
    }

    if (!data || data.markets.length === 0) {
        return (
            <Box sx={{ p: 1.5 }}>
                <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                    No sibling markets for this event.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ px: 2, py: 1.25, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
            <Typography
                sx={{
                    fontSize: '10px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: 'text.secondary',
                    mb: 0.75,
                }}
            >
                {data.markets.length} markets in this event
            </Typography>

            <Box
                component="table"
                sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    '& th': {
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: '9px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                        color: 'text.disabled',
                        py: 0.375,
                        px: 0.75,
                    },
                    '& td': {
                        py: 0.375,
                        px: 0.75,
                        borderTop: 1,
                        borderColor: alpha(theme.palette.divider, 0.2),
                        verticalAlign: 'middle',
                    },
                }}
            >
                <thead>
                    <tr>
                        <th>Market</th>
                        <th style={{ width: 70, textAlign: 'right' }}>Yes prob</th>
                        <th style={{ width: 80, textAlign: 'right' }}>Volume</th>
                        <th style={{ width: 60, textAlign: 'center' }}>Outcome</th>
                        <th style={{ width: 90, textAlign: 'right' }}>Capital</th>
                        <th style={{ width: 70, textAlign: 'right' }}>Traders</th>
                    </tr>
                </thead>
                <tbody>
                    {data.markets.map((m: EventSiblingMarket) => {
                        const cap =
                            m.dominant_side === 'NO'
                                ? m.capital_usd_no
                                : m.capital_usd_yes;
                        const traders =
                            (m.n_traders_yes ?? 0) + (m.n_traders_no ?? 0);
                        return (
                            <tr
                                key={m.market_id}
                                style={{ opacity: m.closed ? 0.5 : 1 }}
                            >
                                <td style={{ fontSize: '11px' }}>
                                    {m.market_question ?? m.outcome_yes_label ?? m.market_id}
                                    {m.closed && (
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 0.75,
                                                fontSize: '9px',
                                                color: 'text.disabled',
                                            }}
                                        >
                                            closed
                                        </Box>
                                    )}
                                </td>
                                <td
                                    style={{
                                        fontSize: '11px',
                                        fontFamily: 'monospace',
                                        textAlign: 'right',
                                    }}
                                >
                                    {formatProb(m.current_yes_prob)}
                                </td>
                                <td
                                    style={{
                                        fontSize: '11px',
                                        fontFamily: 'monospace',
                                        textAlign: 'right',
                                    }}
                                >
                                    {formatUsd(m.volume)}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    {m.has_conviction ? (
                                        <SideBadge side={m.dominant_side} />
                                    ) : (
                                        <Typography
                                            sx={{ fontSize: '10px', color: 'text.disabled' }}
                                        >
                                            —
                                        </Typography>
                                    )}
                                </td>
                                <td
                                    style={{
                                        fontSize: '11px',
                                        fontFamily: 'monospace',
                                        textAlign: 'right',
                                    }}
                                >
                                    {m.has_conviction ? formatUsd(cap) : '—'}
                                </td>
                                <td
                                    style={{
                                        fontSize: '11px',
                                        fontFamily: 'monospace',
                                        textAlign: 'right',
                                    }}
                                >
                                    {m.has_conviction ? formatCount(traders) : '—'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Single conviction row
// ---------------------------------------------------------------------------

function ConvictionRow({
    market,
    metricDef,
    group,
    expanded,
    onToggleExpand,
    activeTags,
    onToggleTag,
    selected,
    onToggleSelect,
    onShowBreakdown,
}: {
    market: ConvictionMarket;
    metricDef: MetricDef | undefined;
    group: string;
    expanded: boolean;
    onToggleExpand: () => void;
    activeTags: string[];
    onToggleTag: (tag: string) => void;
    selected: boolean;
    onToggleSelect: () => void;
    onShowBreakdown: () => void;
}) {
    const theme = useTheme();
    const dominantCapital =
        market.dominant_side === 'NO'
            ? market.capital_usd_no
            : market.capital_usd_yes;
    const traders = market.n_traders_yes + market.n_traders_no;
    const canExpand = market.event_id != null;
    const hasBreakdown = market.breakdown != null;

    // raw_implied is already a capital-weighted YES-side (token0) probability —
    // NO buyers are converted to P(YES) at aggregation time — so it's directly
    // comparable to the market's live YES price. No inversion needed.
    const {
        outcomeMarketProb,
        outcomeImpliedProb,
        avgEntry,
        impliedDelta,
        entryDelta,
    } = computeRowMetrics(market);

    return (
        <>
            <tr
                onClick={canExpand ? onToggleExpand : undefined}
                style={{ cursor: canExpand ? 'pointer' : 'default' }}
            >
                {/* Row selection — feeds the aggregate-delta bar */}
                <td
                    style={{ width: 28, textAlign: 'center' }}
                    onClick={e => e.stopPropagation()}
                >
                    <Checkbox
                        size="small"
                        checked={selected}
                        onChange={onToggleSelect}
                        sx={{ p: 0 }}
                    />
                </td>

                {/* Expand chevron */}
                <td style={{ width: 24, textAlign: 'center' }}>
                    {canExpand &&
                        (expanded ? (
                            <ExpandMoreIcon
                                sx={{ fontSize: 16, color: 'primary.main' }}
                            />
                        ) : (
                            <ChevronRightIcon
                                sx={{ fontSize: 16, color: 'text.disabled' }}
                            />
                        ))}
                </td>

                {/* Market question + the outcome the conviction points to */}
                <td>
                    <EllipsisText
                        text={market.market_question ?? market.market_id}
                        maxWidth={240}
                        sx={{
                            fontSize: '12px',
                            fontWeight: 500,
                            lineHeight: 1.25,
                        }}
                    />
                    {(market.conviction_outcome) && (
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: 'text.secondary',
                                mt: '1px',
                            }}
                        >
                            on {market.conviction_outcome}
                        </Typography>
                    )}
                </td>

                {/* Event */}
                <td>
                    <EllipsisText
                        text={market.event_title ?? '—'}
                        maxWidth={180}
                        sx={{
                            fontSize: '11px',
                            color: 'text.secondary',
                        }}
                    />
                    {market.event_category && (
                        <Typography
                            sx={{
                                fontSize: '9px',
                                color: 'text.disabled',
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em',
                            }}
                        >
                            {market.event_category}
                        </Typography>
                    )}
                </td>

                {/* Tags — clicking a tag toggles it in the filter */}
                <td>
                    {market.polymarket_tags.length > 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                maxWidth: 160,
                            }}
                        >
                            {market.polymarket_tags.slice(0, 4).map(tag => (
                                <TagChip
                                    key={tag}
                                    tag={tag}
                                    active={activeTags.includes(tag)}
                                    onClick={onToggleTag}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: '10px', color: 'text.disabled' }}>
                            —
                        </Typography>
                    )}
                </td>

                {/* Direction the group bet: YES = token0 (the named outcome),
                    NO = token1. */}
                <td style={{ textAlign: 'center' }}>
                    <Typography
                        sx={{
                            fontSize: '11px',
                            color: market.conviction_outcome ? 'text.primary' : 'text.disabled',
                        }}
                    >
                        {market.conviction_outcome ?? '—'}
                    </Typography>
                </td>

                {/* Capital — dominant value + split bar */}
                <td style={{ textAlign: 'right' }}>
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontFamily: 'monospace',
                        }}
                    >
                        {formatUsd(dominantCapital)}
                    </Typography>
                    <CapitalSplitBar
                        yes={market.capital_usd_yes}
                        no={market.capital_usd_no}
                    />
                </td>

                {/* Traders */}
                <td
                    style={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        textAlign: 'right',
                    }}
                >
                    {formatCount(traders)}
                </td>

                {/* Market price — outcome's live YES probability (e.g. P(Spurs win)) */}
                <td
                    style={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        textAlign: 'right',
                    }}
                >
                    {formatProb(outcomeMarketProb)}
                </td>

                {/* Implied — traders' capital-weighted YES probability, with the
                    gap vs the market price in parentheses. */}
                <td
                    style={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {formatProb(outcomeImpliedProb)}
                    {impliedDelta != null && (
                        <Box
                            component="span"
                            sx={{
                                ml: 0.5,
                                fontSize: '10px',
                                color:
                                    impliedDelta >= 0
                                        ? theme.palette.success.main
                                        : theme.palette.error.main,
                            }}
                        >
                            ({formatSignedProb(impliedDelta)})
                        </Box>
                    )}
                </td>

                {/* Avg entry — the price the dominant side paid, with the gap
                    vs the live outcome price in parentheses. */}
                <td
                    style={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {formatProb(avgEntry)}
                    {entryDelta != null && (
                        <Box
                            component="span"
                            sx={{
                                ml: 0.5,
                                fontSize: '10px',
                                color:
                                    entryDelta >= 0
                                        ? theme.palette.success.main
                                        : theme.palette.error.main,
                            }}
                        >
                            ({formatSignedProb(entryDelta)})
                        </Box>
                    )}
                </td>

                {/* Signals */}
                <td style={{ textAlign: 'right' }}>
                    {market.signal_count > 0 ? (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '2px',
                                justifyContent: 'flex-end',
                                color:
                                    market.open_signal_count > 0
                                        ? theme.palette.warning.main
                                        : 'text.secondary',
                            }}
                        >
                            <BoltIcon sx={{ fontSize: 12 }} />
                            <Typography
                                sx={{ fontSize: '11px', fontFamily: 'monospace' }}
                            >
                                {market.open_signal_count}/{market.signal_count}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: '10px', color: 'text.disabled' }}>
                            —
                        </Typography>
                    )}
                </td>

                {/* Metric value (echoed sort_value) */}
                <td
                    style={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        textAlign: 'right',
                    }}
                >
                    {formatMetricValue(metricDef, market.sort_value)}
                </td>

                {/* Updated */}
                <td
                    style={{
                        fontSize: '10px',
                        fontFamily: 'monospace',
                        color: theme.palette.text.secondary,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {formatRelativeTime(market.conviction_ts)}
                </td>

                {/* Full JSON breakdown */}
                <td
                    style={{ width: 28, textAlign: 'center' }}
                    onClick={e => e.stopPropagation()}
                >
                    {hasBreakdown && (
                        <Tooltip title="View full breakdown JSON" arrow>
                            <Box
                                component="span"
                                onClick={onShowBreakdown}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    opacity: 0.35,
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 1 },
                                }}
                            >
                                <DataObjectIcon
                                    sx={{ fontSize: 13, color: 'text.secondary' }}
                                />
                            </Box>
                        </Tooltip>
                    )}
                </td>

                {/* External link to the event on Polymarket */}
                <td style={{ width: 28, textAlign: 'center' }}>
                    {market.event_slug && (
                        <Box
                            component="a"
                            href={`https://polymarket.com/event/${market.event_slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                opacity: 0.35,
                                cursor: 'pointer',
                                '&:hover': { opacity: 1 },
                            }}
                        >
                            <OpenInNewIcon
                                sx={{ fontSize: 12, color: 'text.secondary' }}
                            />
                        </Box>
                    )}
                </td>
            </tr>

            {expanded && canExpand && (
                <tr>
                    <td colSpan={99} style={{ padding: 0 }}>
                        <EventExpandPanel
                            eventId={market.event_id as string}
                            group={group}
                        />
                    </td>
                </tr>
            )}
        </>
    );
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

const selectSx = {
    fontSize: '12px',
    '& .MuiSelect-select': { py: 0.625 },
};

function FilterBar({
    groups,
    group,
    onGroup,
    metric,
    onMetric,
    activeTags,
    onToggleTag,
    activeCategories,
    onToggleCategory,
    minCapitalText,
    onMinCapitalText,
    minTradersText,
    onMinTradersText,
    hasSignal,
    onHasSignal,
    includeTraderScores,
    onIncludeTraderScores,
    limit,
    onLimit,
    onReset,
    resultCount,
}: {
    groups: string[];
    group: string;
    onGroup: (g: string) => void;
    metric: ConvictionMetric;
    onMetric: (m: ConvictionMetric) => void;
    activeTags: string[];
    onToggleTag: (t: string) => void;
    activeCategories: string[];
    onToggleCategory: (c: string) => void;
    minCapitalText: string;
    onMinCapitalText: (v: string) => void;
    minTradersText: string;
    onMinTradersText: (v: string) => void;
    hasSignal: boolean;
    onHasSignal: (v: boolean) => void;
    includeTraderScores: boolean;
    onIncludeTraderScores: (v: boolean) => void;
    limit: number;
    onLimit: (n: number) => void;
    onReset: () => void;
    resultCount: number | null;
}) {
    // Group metric options under their family for a readable dropdown.
    const metricGroups = useMemo(() => {
        const order: MetricDef['group'][] = ['Capital', 'Traders', 'Edge'];
        return order.map(g => ({
            group: g,
            items: CONVICTION_METRICS.filter(m => m.group === g),
        }));
    }, []);

    const hasActiveFilters =
        activeTags.length > 0 ||
        activeCategories.length > 0 ||
        minCapitalText !== '' ||
        minTradersText !== '' ||
        hasSignal;

    return (
        <SectionCard sx={{ mx: 2, mb: 1.5 }}>
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {/* Row 1 — primary controls */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={labelSx}>Group</Typography>
                        <Select
                            size="small"
                            value={group}
                            onChange={e => onGroup(e.target.value)}
                            sx={{ ...selectSx, minWidth: 130 }}
                        >
                            {groups.map(g => (
                                <MenuItem key={g} value={g} sx={{ fontSize: '12px' }}>
                                    {g}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={labelSx}>Rank by</Typography>
                        <Select
                            size="small"
                            value={metric}
                            onChange={e => onMetric(e.target.value as ConvictionMetric)}
                            sx={{ ...selectSx, minWidth: 160 }}
                        >
                            {metricGroups.flatMap(mg => [
                                <ListSubheader
                                    key={`h-${mg.group}`}
                                    sx={{
                                        fontSize: '9px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                        lineHeight: 2,
                                    }}
                                >
                                    {mg.group}
                                </ListSubheader>,
                                ...mg.items.map(m => (
                                    <MenuItem
                                        key={m.value}
                                        value={m.value}
                                        sx={{ fontSize: '12px' }}
                                    >
                                        {m.label}
                                    </MenuItem>
                                )),
                            ])}
                        </Select>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={labelSx}>Min capital ($)</Typography>
                        <TextField
                            size="small"
                            type="number"
                            placeholder="0"
                            value={minCapitalText}
                            onChange={e => onMinCapitalText(e.target.value)}
                            sx={{ width: 110, '& input': { fontSize: '12px', py: 0.75 } }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={labelSx}>Min traders</Typography>
                        <TextField
                            size="small"
                            type="number"
                            placeholder="1"
                            value={minTradersText}
                            onChange={e => onMinTradersText(e.target.value)}
                            sx={{ width: 90, '& input': { fontSize: '12px', py: 0.75 } }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={labelSx}>Signals</Typography>
                        <ToggleButton
                            value="has_signal"
                            size="small"
                            selected={hasSignal}
                            onChange={() => onHasSignal(!hasSignal)}
                            sx={{
                                fontSize: '11px',
                                textTransform: 'none',
                                py: 0.5,
                                px: 1,
                                gap: 0.5,
                            }}
                        >
                            <BoltIcon sx={{ fontSize: 13 }} />
                            Has signals
                        </ToggleButton>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={labelSx}>Trader scores</Typography>
                        <Tooltip
                            title="Include the full per-trader breakdown. Heavier payload — leave off unless you need it."
                            arrow
                        >
                            <Box
                                onClick={() => onIncludeTraderScores(!includeTraderScores)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.25,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    height: 32,
                                }}
                            >
                                <Checkbox
                                    size="small"
                                    checked={includeTraderScores}
                                    onChange={() => onIncludeTraderScores(!includeTraderScores)}
                                    sx={{ p: 0.25 }}
                                />
                                <Typography sx={{ fontSize: '11px' }}>Include</Typography>
                            </Box>
                        </Tooltip>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography sx={labelSx}>Show</Typography>
                        <Select
                            size="small"
                            value={limit}
                            onChange={e => onLimit(Number(e.target.value))}
                            sx={{ ...selectSx, minWidth: 80 }}
                        >
                            {[50, 100, 200].map(n => (
                                <MenuItem key={n} value={n} sx={{ fontSize: '12px' }}>
                                    {n}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {resultCount != null && (
                            <Typography
                                sx={{ fontSize: '11px', color: 'text.secondary' }}
                            >
                                {resultCount} market{resultCount === 1 ? '' : 's'}
                            </Typography>
                        )}
                        {hasActiveFilters && (
                            <Typography
                                onClick={onReset}
                                sx={{
                                    fontSize: '11px',
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Clear filters
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Row 2 — category filters */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ ...labelSx, mr: 0.25 }}>Categories</Typography>
                    {VALID_EVENT_CATEGORIES.map(c => (
                        <TagChip
                            key={c}
                            tag={c}
                            active={activeCategories.includes(c)}
                            onClick={onToggleCategory}
                        />
                    ))}
                </Box>

                {/* Row 3 — polymarket tag filters */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ ...labelSx, mr: 0.25 }}>Tags</Typography>
                    {VALID_POLYMARKET_TAGS.map(t => (
                        <TagChip
                            key={t}
                            tag={t}
                            active={activeTags.includes(t)}
                            onClick={onToggleTag}
                        />
                    ))}
                </Box>
            </Box>
        </SectionCard>
    );
}

const labelSx = {
    fontSize: '9px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'text.secondary',
} as const;

// ---------------------------------------------------------------------------
// Results table
// ---------------------------------------------------------------------------

// Sticky bar height — thead sticks just below it.
const AGG_BAR_HEIGHT = 46;

function ResultsTable({
    markets,
    metricDef,
    group,
    activeTags,
    onToggleTag,
}: {
    markets: ConvictionMarket[];
    metricDef: MetricDef | undefined;
    group: string;
    activeTags: string[];
    onToggleTag: (t: string) => void;
}) {
    const theme = useTheme();
    const [sortKey, setSortKey] = useState<ClientSortKey | null>(null);
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [breakdownMarket, setBreakdownMarket] = useState<ConvictionMarket | null>(
        null
    );

    function handleSort(key: ClientSortKey) {
        if (sortKey === key) {
            if (sortDir === 'desc') setSortDir('asc');
            else {
                setSortKey(null);
                setSortDir('desc');
            }
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    }

    // null sortKey = preserve server ranking.
    const rows = useMemo(() => {
        if (!sortKey) return markets;
        return [...markets].sort((a, b) => {
            const av = clientSortValue(a, sortKey);
            const bv = clientSortValue(b, sortKey);
            return sortDir === 'asc' ? av - bv : bv - av;
        });
    }, [markets, sortKey, sortDir]);

    const toggleSelect = useCallback((id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const allSelected = rows.length > 0 && selectedIds.size >= rows.length;
    const someSelected = selectedIds.size > 0 && !allSelected;

    const toggleSelectAll = useCallback(() => {
        setSelectedIds(prev =>
            prev.size >= rows.length ? new Set() : new Set(rows.map(r => r.market_id))
        );
    }, [rows]);

    // Aggregate deltas — over the selected rows, or every filtered row when the
    // selection is empty. impliedDelta = live price − implied; entryDelta =
    // live price − avg entry price.
    const agg = useMemo(() => {
        const source =
            selectedIds.size > 0
                ? rows.filter(r => selectedIds.has(r.market_id))
                : rows;

        const implied: number[] = [];
        const entry: number[] = [];
        for (const m of source) {
            const { impliedDelta, entryDelta } = computeRowMetrics(m);
            if (impliedDelta != null) implied.push(impliedDelta);
            if (entryDelta != null) entry.push(entryDelta);
        }
        const sum = (xs: number[]) =>
            xs.length ? xs.reduce((a, b) => a + b, 0) : null;
        return {
            scope: selectedIds.size > 0 ? 'selected' : 'all',
            count: source.length,
            impliedMean: mean(implied),
            impliedSum: sum(implied),
            impliedN: implied.length,
            entryMean: mean(entry),
            entrySum: sum(entry),
            entryN: entry.length,
        };
    }, [rows, selectedIds]);

    const thProps = { currentKey: sortKey, currentDir: sortDir, onSort: handleSort };

    return (
        <Box>
            <AggregateBar
                agg={agg}
                hasSelection={selectedIds.size > 0}
                onClearSelection={() => setSelectedIds(new Set())}
            />

            <Box
                component="table"
                sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    '& th': {
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: '9.5px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'text.secondary',
                        py: 0.5,
                        px: 0.75,
                        borderBottom: 1,
                        borderColor: 'divider',
                        position: 'sticky',
                        top: AGG_BAR_HEIGHT,
                        bgcolor: 'background.paper',
                        zIndex: 1,
                    },
                    '& td': {
                        py: 0.625,
                        px: 0.75,
                        borderBottom: 1,
                        borderColor: alpha(theme.palette.divider, 0.25),
                        verticalAlign: 'middle',
                    },
                    '& tbody tr': {
                        transition: 'background 0.1s',
                        '&:hover td': { bgcolor: 'action.hover' },
                    },
                }}
            >
                <thead>
                    <tr>
                        <th style={{ width: 28, textAlign: 'center' }}>
                            <Checkbox
                                size="small"
                                checked={allSelected}
                                indeterminate={someSelected}
                                onChange={toggleSelectAll}
                                sx={{ p: 0 }}
                            />
                        </th>
                        <th style={{ width: 24 }} />
                        <th style={{ width: 248 }}>Market</th>
                        <th style={{ width: 188 }}>Event</th>
                        <th style={{ width: 170 }}>Tags</th>
                        <th style={{ width: 56, textAlign: 'center' }}>Outcome</th>
                        <SortableTh
                            label="Capital"
                            sortKey="capital_usd_yes"
                            style={{ width: 96, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="Traders"
                            sortKey="trader_count"
                            style={{ width: 72, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="Market %"
                            sortKey="current_yes_prob"
                            style={{ width: 72, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="Implied %"
                            sortKey="raw_implied"
                            style={{ width: 110, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="Avg entry"
                            sortKey="avg_entry"
                            style={{ width: 110, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label="Signals"
                            sortKey="signal_count"
                            style={{ width: 72, textAlign: 'right' }}
                            {...thProps}
                        />
                        <SortableTh
                            label={metricDef?.label ?? 'Metric'}
                            sortKey="sort_value"
                            style={{ width: 96, textAlign: 'right' }}
                            {...thProps}
                        />
                        <th style={{ width: 84, textAlign: 'right' }}>Updated</th>
                        <th style={{ width: 28 }} />
                        <th style={{ width: 28 }} />
                    </tr>
                </thead>
                <tbody>
                    {rows.map(market => (
                        <ConvictionRow
                            key={market.market_id}
                            market={market}
                            metricDef={metricDef}
                            group={group}
                            expanded={expandedId === market.market_id}
                            onToggleExpand={() =>
                                setExpandedId(prev =>
                                    prev === market.market_id ? null : market.market_id
                                )
                            }
                            activeTags={activeTags}
                            onToggleTag={onToggleTag}
                            selected={selectedIds.has(market.market_id)}
                            onToggleSelect={() => toggleSelect(market.market_id)}
                            onShowBreakdown={() => setBreakdownMarket(market)}
                        />
                    ))}
                </tbody>
            </Box>

            <BreakdownDialog
                market={breakdownMarket}
                onClose={() => setBreakdownMarket(null)}
            />
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Aggregate-delta bar — sticky strip summarising the gap between the live
// outcome price and (a) the implied probability and (b) the avg entry price,
// averaged across the selected rows (or all filtered rows when none selected).
// ---------------------------------------------------------------------------

interface AggregateSummary {
    scope: string;
    count: number;
    impliedMean: number | null;
    impliedSum: number | null;
    impliedN: number;
    entryMean: number | null;
    entrySum: number | null;
    entryN: number;
}

function AggDeltaStat({
    label,
    mean,
    sum,
    n,
}: {
    label: string;
    mean: number | null;
    sum: number | null;
    n: number;
}) {
    const theme = useTheme();
    const color =
        mean == null
            ? theme.palette.text.disabled
            : mean >= 0
                ? theme.palette.success.main
                : theme.palette.error.main;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
            <Typography sx={{ ...labelSx, color: 'text.disabled' }}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                <Typography
                    sx={{ fontSize: '14px', fontWeight: 700, fontFamily: 'monospace', color }}
                >
                    {mean == null ? '—' : formatSignedProb(mean)}
                </Typography>
                <Typography
                    sx={{ fontSize: '10px', color: 'text.secondary', fontFamily: 'monospace' }}
                >
                    Σ {sum == null ? '—' : formatSignedProb(sum)} · n={n}
                </Typography>
            </Box>
        </Box>
    );
}

function AggregateBar({
    agg,
    hasSelection,
    onClearSelection,
}: {
    agg: AggregateSummary;
    hasSelection: boolean;
    onClearSelection: () => void;
}) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 3,
                height: AGG_BAR_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
                px: 1,
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 96 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>
                    Aggregate Δ
                </Typography>
                <Box
                    component="span"
                    sx={{
                        fontSize: '9.5px',
                        fontWeight: 600,
                        letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                        color: hasSelection ? theme.palette.primary.main : 'text.secondary',
                        border: 1,
                        borderColor: hasSelection
                            ? alpha(theme.palette.primary.main, 0.5)
                            : 'divider',
                        bgcolor: hasSelection
                            ? alpha(theme.palette.primary.main, 0.08)
                            : 'transparent',
                        borderRadius: 0.75,
                        px: 0.625,
                        py: '1px',
                    }}
                >
                    {hasSelection ? `${agg.count} selected` : `all ${agg.count}`}
                </Box>
            </Box>

            <AggDeltaStat
                label="Market − Implied"
                mean={agg.impliedMean}
                sum={agg.impliedSum}
                n={agg.impliedN}
            />
            <AggDeltaStat
                label="Market − Avg entry"
                mean={agg.entryMean}
                sum={agg.entrySum}
                n={agg.entryN}
            />

            {hasSelection && (
                <Typography
                    onClick={onClearSelection}
                    sx={{
                        ml: 'auto',
                        fontSize: '11px',
                        color: 'primary.main',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    Clear selection
                </Typography>
            )}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Full breakdown JSON dialog
// ---------------------------------------------------------------------------

function BreakdownDialog({
    market,
    onClose,
}: {
    market: ConvictionMarket | null;
    onClose: () => void;
}) {
    const theme = useTheme();
    const json = useMemo(
        () => (market ? JSON.stringify(market.breakdown ?? {}, null, 2) : ''),
        [market]
    );

    const copy = useCallback(() => {
        if (json) navigator.clipboard?.writeText(json);
    }, [json]);

    return (
        <Dialog
            open={market != null}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { maxHeight: '80vh' } }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '13px',
                    fontWeight: 700,
                    pr: 1,
                }}
            >
                <DataObjectIcon sx={{ fontSize: 16 }} />
                <Box
                    component="span"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                    }}
                >
                    {market?.market_question ?? market?.market_id ?? 'Breakdown'}
                </Box>
                <Tooltip title="Copy JSON" arrow>
                    <IconButton size="small" onClick={copy}>
                        <ContentCopyIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                <Box
                    component="pre"
                    sx={{
                        m: 0,
                        p: 1.5,
                        fontSize: '11px',
                        lineHeight: 1.5,
                        fontFamily: 'monospace',
                        whiteSpace: 'pre',
                        overflow: 'auto',
                        color: theme.palette.text.primary,
                        ...hoverScrollbar,
                    }}
                >
                    {json}
                </Box>
            </DialogContent>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Small debounce hook for the numeric inputs
// ---------------------------------------------------------------------------

function useDebounced<T>(value: T, delay = 400): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

function toggleInArray(arr: string[], item: string): string[] {
    return arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];
}

// ---------------------------------------------------------------------------
// ConvictionScreenerView
// ---------------------------------------------------------------------------

export function ConvictionScreenerView() {
    const [searchParams, setSearchParams] = useSearchParams();

    // Group + metric are persisted in the URL so a screen is shareable.
    const groupParam = searchParams.get('group') ?? 'insider';
    const metricParam = (searchParams.get('metric') as ConvictionMetric) ?? DEFAULT_METRIC;

    // Populate the group dropdown from the existing global watchlists, falling
    // back to the two groups the screener defaults to.
    const { data: watchlistsData } = useGlobalWatchlists();
    const groups = useMemo(() => {
        const fromApi = watchlistsData?.watchlists.map(w => w.group_name) ?? [];
        const merged = Array.from(new Set([...fromApi, 'insider', 'winner', 'all_traders']));
        return merged;
    }, [watchlistsData]);

    // Filter state
    const [activeTags, setActiveTags] = useState<string[]>([]);
    const [activeCategories, setActiveCategories] = useState<string[]>([]);
    const [minCapitalText, setMinCapitalText] = useState('');
    const [minTradersText, setMinTradersText] = useState('');
    const [hasSignal, setHasSignal] = useState(false);
    const [includeTraderScores, setIncludeTraderScores] = useState(false);
    const [limit, setLimit] = useState(50);

    const minCapital = useDebounced(minCapitalText);
    const minTraders = useDebounced(minTradersText);

    const params = useMemo(
        () => ({
            group: groupParam,
            sortBy: metricParam,
            polymarketTags: activeTags,
            eventCategories: activeCategories,
            minCapital: minCapital !== '' ? Number(minCapital) : null,
            minTraders: minTraders !== '' ? Number(minTraders) : null,
            hasSignal,
            includeTraderScores,
            limit,
        }),
        [
            groupParam,
            metricParam,
            activeTags,
            activeCategories,
            minCapital,
            minTraders,
            hasSignal,
            includeTraderScores,
            limit,
        ]
    );

    const { data, loading, error } = useConvictionScreener(params);

    const metricDef = useMemo(
        () => CONVICTION_METRICS.find(m => m.value === metricParam),
        [metricParam]
    );

    const setGroup = useCallback(
        (g: string) => {
            const next = new URLSearchParams(searchParams);
            next.set('group', g);
            setSearchParams(next);
        },
        [searchParams, setSearchParams]
    );

    const setMetric = useCallback(
        (m: ConvictionMetric) => {
            const next = new URLSearchParams(searchParams);
            next.set('metric', m);
            setSearchParams(next);
        },
        [searchParams, setSearchParams]
    );

    const toggleTag = useCallback(
        (t: string) => setActiveTags(prev => toggleInArray(prev, t)),
        []
    );
    const toggleCategory = useCallback(
        (c: string) => setActiveCategories(prev => toggleInArray(prev, c)),
        []
    );

    const resetFilters = useCallback(() => {
        setActiveTags([]);
        setActiveCategories([]);
        setMinCapitalText('');
        setMinTradersText('');
        setHasSignal(false);
    }, []);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
            }}
        >
            {/* Page header */}
            <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, flexShrink: 0 }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
                    Conviction Screener
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.25 }}>
                    Rank live markets by where smart money is positioned
                </Typography>
            </Box>

            <FilterBar
                groups={groups}
                group={groupParam}
                onGroup={setGroup}
                metric={metricParam}
                onMetric={setMetric}
                activeTags={activeTags}
                onToggleTag={toggleTag}
                activeCategories={activeCategories}
                onToggleCategory={toggleCategory}
                minCapitalText={minCapitalText}
                onMinCapitalText={setMinCapitalText}
                minTradersText={minTradersText}
                onMinTradersText={setMinTradersText}
                hasSignal={hasSignal}
                onHasSignal={setHasSignal}
                includeTraderScores={includeTraderScores}
                onIncludeTraderScores={setIncludeTraderScores}
                limit={limit}
                onLimit={setLimit}
                onReset={resetFilters}
                resultCount={data?.markets.length ?? null}
            />

            {/* Scrollable results */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 2,
                    pb: 2,
                    ...hoverScrollbar,
                }}
            >
                {loading && (
                    <Box sx={{ pt: 1 }}>
                        {[...Array(8)].map((_, i) => (
                            <Skeleton
                                key={i}
                                variant="rectangular"
                                height={34}
                                sx={{ mb: 0.5, borderRadius: 0.5 }}
                            />
                        ))}
                    </Box>
                )}

                {error && !loading && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                            Couldn't load the screener. Adjust a filter and try again.
                        </Typography>
                    </Box>
                )}

                {!loading && !error && data && data.markets.length === 0 && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                            No markets match these filters
                        </Typography>
                        <Typography
                            sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}
                        >
                            Loosen the capital or trader minimums, or clear a tag.
                        </Typography>
                    </Box>
                )}

                {!loading && !error && data && data.markets.length > 0 && (
                    <ResultsTable
                        markets={data.markets}
                        metricDef={metricDef}
                        group={groupParam}
                        activeTags={activeTags}
                        onToggleTag={toggleTag}
                    />
                )}
            </Box>
        </Box>
    );
}